import {
  ICalendarState,
  ICountUsersResponse,
  IUser,
  ICalendarStatisticsState,
  IMessageBoardState,
} from '../../lib/interfaces';
import { GeneralAction } from '../actions/general';

export interface GeneralState {
  loading: boolean;
  apiLoading: boolean;
  isLoggingOut: boolean;
  loginError: string | null;
  apiError: string | null;
  sessionUser: IUser | null;
  patients: IUser[];
  patientDetails: IUser | null;
  usersCount: ICountUsersResponse | null;
  calendar: ICalendarState;
  calendarStatistics: ICalendarStatisticsState;
  patientDetailsLoading: boolean;
  messageBoard: IMessageBoardState;
}

const initialState: GeneralState = {
  loading: false,
  isLoggingOut: false,
  apiLoading: false,
  loginError: null,
  apiError: null,
  sessionUser: null,
  patients: [],
  patientDetails: null,
  usersCount: null,
  calendar: {
    diaryEntries: [],
    startDate: null,
    endDate: null,
  },
  calendarStatistics: {
    statisticsEntries: [],
    statisticsLoader: false,
    exportButtonLoader: false,
    file: null,
  },
  patientDetailsLoading: false,
  messageBoard: {
    messages: [],
    messageContent: '',
  },
};

const generalReducer = (
  // eslint-disable-next-line default-param-last -- Redux reducer signature
  previousState = initialState,
  action: GeneralAction
): GeneralState => {
  switch (action.type) {
    case 'LOGOUT':
      return {
        ...initialState,
      };
    case 'SET_IS_LOGGING_OUT':
      return {
        ...previousState,
        isLoggingOut: action.payload,
      };
    case 'LOGIN_STARTED':
      return {
        ...previousState,
        loading: true,
        loginError: null,
      };
    case 'LOGIN_COMPLETED':
      return {
        ...previousState,
        loading: false,
        loginError: null,
      };
    case 'SESSION_USER_FETCH_COMPLETED':
      return {
        ...previousState,
        loading: false,
        sessionUser: action.payload.user,
      };
    case 'SESSION_USER_FETCH_FAILED':
      return {
        ...previousState,
        loading: false,
        loginError: action.payload.error,
      };
    case 'LOGIN_FAILED':
      return {
        ...previousState,
        loading: false,
        loginError: action.payload.error,
      };
    case 'LOADER_TRUE':
      return {
        ...previousState,
        loading: true,
      };
    case 'LOADER_FALSE':
      return {
        ...previousState,
        loading: false,
      };
    case 'GET_ALL_PATIENTS_STARTED':
      return {
        ...previousState,
        apiLoading: true,
      };
    case 'GET_ALL_PATIENTS_COMPLETED':
      return {
        ...previousState,
        patients: action.payload.patients,
        apiLoading: false,
      };
    case 'GET_ALL_PATIENTS_FAILED':
      return {
        ...previousState,
        apiError: action.payload.error,
        apiLoading: false,
      };
    case 'GET_PATIENT_DETAILS_STARTED':
      return {
        ...previousState,
        apiLoading: true,
      };
    case 'GET_PATIENT_DETAILS_COMPLETED':
      return {
        ...previousState,
        patientDetails: action.payload.patient,
        apiLoading: false,
      };
    case 'GET_PATIENT_DETAILS_FAILED':
      return {
        ...previousState,
        apiError: action.payload.error,
        apiLoading: false,
      };
    case 'UPDATE_USER_PROFILE_STARTED':
      return {
        ...previousState,
        apiLoading: true,
      };
    case 'UPDATE_USER_PROFILE_COMPLETED':
      return {
        ...previousState,
        patientDetails: action.payload.updatedUser,
        apiLoading: false,
      };
    case 'UPDATE_USER_PROFILE_FAILED':
      return {
        ...previousState,
        apiError: action.payload.error,
        apiLoading: false,
      };
    case 'COUNT_USERS_STARTED':
      return {
        ...previousState,
        apiLoading: true,
      };
    case 'COUNT_USERS_COMPLETED':
      return {
        ...previousState,
        usersCount: action.payload.usersCount,
        apiLoading: false,
      };
    case 'COUNT_USERS_FAILED':
      return {
        ...previousState,
        apiError: action.payload.error,
        apiLoading: false,
      };
    case 'GET_DIARY_PERIOD_STATISTICS_STARTED':
      return {
        ...previousState,
        calendarStatistics: {
          ...previousState.calendarStatistics,
          statisticsLoader: true,
        },
        patientDetailsLoading: true,
      };
    case 'GET_DIARY_PERIOD_STATISTICS_COMPLETED':
      return {
        ...previousState,
        calendarStatistics: {
          ...previousState.calendarStatistics,
          statisticsEntries: [...action.payload.diaryEntries],
          statisticsLoader: false,
        },
        patientDetailsLoading: false,
      };
    case 'GET_DIARY_PERIOD_STATISTICS_FAILED':
      return {
        ...previousState,
        calendarStatistics: {
          ...previousState.calendarStatistics,
          statisticsLoader: false,
        },
        apiError: action.payload.error,
        patientDetailsLoading: false,
      };
    case 'GET_DIARY_PERIOD_STARTED':
      return {
        ...previousState,
        patientDetailsLoading: true,
      };
    case 'GET_DIARY_PERIOD_COMPLETED':
      return {
        ...previousState,
        calendar: {
          ...previousState.calendar,
          diaryEntries: [...action.payload.diaryEntries],
          startDate: action.payload.startDate
            ? new Date(action.payload.startDate)
            : null,
          endDate: action.payload.endDate
            ? new Date(action.payload.endDate)
            : null,
        },
        patientDetailsLoading: false,
      };
    case 'GET_DIARY_PERIOD_FAILED':
      return {
        ...previousState,
        apiError: action.payload.error,
        patientDetailsLoading: false,
      };
    case 'ADD_NEW_USER_STARTED':
      return {
        ...previousState,
        apiLoading: true,
        apiError: '',
      };
    case 'ADD_NEW_USER_COMPLETED':
      return {
        ...previousState,
        patients: previousState.patients.concat(action.payload.newAdmin),
        apiLoading: false,
      };
    case 'ADD_NEW_USER_FAILED':
      return {
        ...previousState,
        apiError: action.payload.error,
        apiLoading: false,
      };
    case 'GET_STATISTICS_FILE_STARTED':
      return {
        ...previousState,
        calendarStatistics: {
          ...previousState.calendarStatistics,
          exportButtonLoader: true,
        },
      };
    case 'GET_STATISTICS_FILE_COMPLETED':
      return {
        ...previousState,
        calendarStatistics: {
          ...previousState.calendarStatistics,
          exportButtonLoader: false,
        },
      };
    case 'GET_STATISTICS_FILE_FAILED':
      return {
        ...previousState,
        calendarStatistics: {
          ...previousState.calendarStatistics,
          exportButtonLoader: false,
        },
        apiError: action.payload.error,
      };
    default:
      return previousState;
  }
};

export default generalReducer;
